import { useCallback, useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { PublicNoticeMutations } from '@api/services/public-notices';
import { FileStorageMutations } from '@api/services/file-storage';
import { connect } from 'redux-bundler-react';
import { Alert, Button, Grid, Label } from '@trussworks/react-uswds';
import LoaderButton from '@components/loader/LoaderButton';
import { toast } from 'react-toastify';

import Antispam from '@components/antispam/Antispam';
import ErrorSummary from '@components/error-summary/ErrorSummary';
import FileInput from '@components/file-input/FileInput';
import PhoneInput from '@components/phone-input/PhoneInput';
import TextInput from '@components/text-input/TextInput';
import TextArea from '@components/textarea/TextArea';
import Tooltip from '@components/tooltip/tooltip';

import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';

import { ErrorMessages } from '@src/utils/enums';
import { countryCodeRegex, phoneExtRegex, phoneRegex, usPhoneRegex } from '@src/utils/regex';
import { contactInputValidation } from '@src/utils/validation/inputValidation.contact';

import '../publicNotices.scss';
import { parseApiValidationErrors } from '@src/api/helpers';
import { usePublicNotices, useModal, useAntispam } from '@stores';

const phoneOptionalDependencies = [
  //     1                   2                3
  ['phoneType', 'phoneCountryCode', 'phone'], // 1 2 3
  ['phoneType', 'phone', 'phoneCountryCode'], // 1 3 2
  ['phoneCountryCode', 'phoneType', 'phone'], // 2 1 3
  ['phoneCountryCode', 'phone', 'phoneType'], // 2 3 1
  ['phone', 'phoneType', 'phoneCountryCode'], // 3 1 2
  ['phone', 'phoneCountryCode', 'phoneType'], // 3 2 1
];

const defaultValues = {
  antispam: '',
  comments: '',
  company: '',
  emailAddress: '',
  firstName: '',
  lastName: '',
  phone: '',
  phoneCountryCode: '1',
  phoneExtension: '',
  phoneType: '',
};

const tooltipContent = (
  <>
    <p>
      To select multiple files, while pressing down the control key (Ctrl) click on files that you want to select.
      Continue to press down the control key until you select all the files you want.
    </p>
    <p>
      <span className='text-bold'>{ErrorMessages.UploadNotice}</span>
    </p>
  </>
);

const SecurityCheckTooltipContent = <p>This simple math equation is used to prove you are a human and not a robot.</p>;

const cleanPhoneInputData = (contact) => {
  let updatedContact = { ...contact };

  // Check phone and remove country code if empty
  if (!contact?.phone || contact?.phone?.trim() === '') {
    updatedContact.phoneCountryCode = '';
    updatedContact.phoneType = '';
  }

  return updatedContact;
};

const PublicNoticeComment = connect(({ onClose, publicNoticeData }) => {
  const [stagedFiles, setStagedFiles] = useState([]);
  const [fileErrors, setFileErrors] = useState(false);
  const { closeModal } = useModal();
  const { apiValidationErrors, clearApiValidationErrors, setApiValidationErrors } = usePublicNotices();
  const { isValid: antispamResults } = useAntispam();
  const isAcceptPublicComments = publicNoticeData?.acceptPublicComments;

  const { mutate: saveAttachments, isPending: isSavingAttachments } = useMutation({
    mutationFn: FileStorageMutations.uploadPnAttachments,
    onSuccess: () => {
      toast.success('Comment successfully submitted!');
      closeModal();
    },
    onError: () => {
      toast.error('Failed to save attachments.');
    },
  });

  const { mutate: saveComment, isPending: isSaving } = useMutation({
    mutationFn: PublicNoticeMutations.saveComment,
    onSuccess: (response) => {
      clearApiValidationErrors();
      const validationErrors = parseApiValidationErrors(response);

      if (validationErrors?.length > 0) {
        setApiValidationErrors(validationErrors);
      } else if (stagedFiles?.length > 0) {
        const { publicNoticeCommentID } = response?.data;
        saveAttachments({
          commentId: publicNoticeCommentID,
          daNumber: publicNoticeData.daNumber,
          publicNoticeId: publicNoticeData.publicNoticeID,
          fileData: stagedFiles,
        });
      } else {
        toast.success('Comment successfully submitted!');
        closeModal();
      }
    },
    onError: () => {
      toast.error('Failed to save comments.');
    },
  });

  const schema = yup.object().shape(
    {
      ...contactInputValidation.nameRequired,
      ...contactInputValidation.emailOptional,
      phoneType: yup.string().when('phone', {
        is: (phone) => !!phone?.length,
        then: (schema) => schema.required(ErrorMessages.Required),
        otherwise: (schema) => schema.nullable(),
      }),
      phoneCountryCode: yup.string().when(['phoneType', 'phone'], {
        is: (type, phone) => !!type?.length || !!phone?.length,
        then: (schema) => schema.required(ErrorMessages.Required).matches(countryCodeRegex, ErrorMessages.Invalid),
        otherwise: (schema) => schema.nullable(),
      }),
      phone: yup.string().when(['phoneType', 'phoneCountryCode'], {
        is: (type, code) => type && type !== '1' && (type?.length > 0 || code?.length > 0), // Require phone only if type exists, isn't 1, and either type or code is present
        then: (schema) =>
          schema.when('phoneCountryCode', {
            is: (code) => code === '1',
            then: (schema) => schema.matches(usPhoneRegex, { message: ErrorMessages.Invalid }).required(),
            otherwise: (schema) => schema.matches(phoneRegex, { message: ErrorMessages.Invalid }).required(),
          }),
        otherwise: (schema) => schema.nullable(), // Allow null/empty if condition isn't met
      }),
      phoneExtension: yup
        .string()
        .nullable()
        .matches(phoneExtRegex, { message: ErrorMessages.Invalid, excludeEmptyString: true }),
      company: yup.string().nullable(),
      comments: yup.string().required(ErrorMessages.Required),
      attachments: yup.string().test('file-invalid-test', 'File selected is invalid', function (value) {
        return value !== 'error';
      }),
      antispam: yup
        .string()
        .required('Please solve the equation')
        .test('antispam', 'Invalid response for the math equation', (value) => value && antispamResults),
    },
    [...phoneOptionalDependencies]
  );

  const methods = useForm({
    defaultValues: defaultValues,
    resolver: yupResolver(schema),
    mode: 'onBlur',
    stateOptions: [],
  });
  const {
    formState: { errors, isValid },
    setValue,
    setFocus,
    watch,
    getValues,
    trigger,
  } = methods;
  const phone = watch('phone');
  const attachments = watch('attachments');

  const handleAntispamBlur = () => {
    trigger('antispam');
  };

  const handleDeleteFile = () => {
    setStagedFiles([]);
    setValue('attachments', '', { shouldValidate: true });
  };

  const handleFileInput = (e) => {
    const filesArr = Array.from(e?.target?.files) ?? [];
    setStagedFiles(filesArr);
    trigger('attachments');
  };

  const handleSave = async () => {
    if (isValid && fileErrors === false && antispamResults) {
      if (!isAcceptPublicComments) {
        toast.error('Not accepting comments for this public notice');
      } else {
        const formData = getValues();
        const parsedPhone = phone?.replace(/\D/g, '') ?? undefined;
        const cleanPhoneValues = cleanPhoneInputData(formData);

        saveComment({
          ...cleanPhoneValues,
          publicNoticeID: publicNoticeData?.publicNoticeID,
          phone: parsedPhone,
          daNumber: publicNoticeData?.daNumber,
        });
      }
    } else {
      trigger();
    }
  };

  const handleClose = () => {
    clearApiValidationErrors();
    onClose();
  };

  const setFileValue = useCallback(() => {
    if (attachments?.length === 0) {
      return null;
    }
    return fileErrors === true ? 'error' : 'true';
  }, [attachments?.length, fileErrors]);

  // Set attachments value
  useEffect(() => {
    setValue('attachments', setFileValue(), { shouldValidate: true });
  }, [fileErrors, setValue, setFileValue]);

  useEffect(() => {
    setFocus(errors?.[Object.keys(errors)[0]]?.['ref']?.['id']);
  }, [errors, setFocus]);

  return (
    <>
      <FormProvider {...methods}>
        {errors && <ErrorSummary errors={errors} modalID='submitCommentsModal' type='modal' />}
        {apiValidationErrors.length > 0 && <ErrorSummary errors={apiValidationErrors} type='api-validation' />}
        <section className='modal-body' id='submitCommentsModal'>
          <div>
            <section className='info-section'>
              <p className='margin-bottom-0'>
                DA Number: <span className='text-bold'>{publicNoticeData?.daNumber}</span>
              </p>
              <p className='margin-bottom-0'>
                Project: <span className='text-bold'>{publicNoticeData?.projectName}</span>
              </p>
            </section>
            <Grid row>
              <Grid col>
                <Alert noIcon slim className='callout margin-top-2'>
                  Please enter contact information below if you would like to receive an acknowledgment email regarding
                  this public notice. If contact information is not provided, USACE will not be able to provide a
                  response email concerning this public notice.
                </Alert>
              </Grid>
            </Grid>
            <Grid row gap='md'>
              <Grid tablet={{ col: 6 }}>
                <TextInput name='firstName' label='First Name' required />
              </Grid>
              <Grid tablet={{ col: 6 }}>
                <TextInput name='lastName' label='Last Name' required />
              </Grid>
            </Grid>
            <Grid row>
              <Grid col>
                <TextInput name='emailAddress' label='Email' />
              </Grid>
            </Grid>
            <Grid row>
              <Grid col>
                <PhoneInput
                  phoneName='phone'
                  phoneCountryCodeName='phoneCountryCode'
                  phoneTypeName='phoneType'
                  extensionName='phoneExtension'
                  label='Phone Number'
                  showTypeSelect
                />
              </Grid>
            </Grid>
            <Grid row>
              <Grid col>
                <TextInput name='company' label='Company Name' />
              </Grid>
            </Grid>
            <Grid row>
              <Grid col>
                <Alert noIcon slim className='callout margin-top-2'>
                  For lengthy comments that you may wish to provide through an attachment - Please summarize the main
                  points of your attachment here to better help USACE understand the contents and to categorize comments
                  for transparency and accessibility. It is recommended to limit attachments of supplementary data to
                  images, tables, or graphs.
                </Alert>
              </Grid>
            </Grid>
            <Grid row>
              <Grid col>
                <TextArea
                  name='comments'
                  label='Summary of Comments'
                  tooltip='Comments over 4000 characters should be uploaded as a file.'
                  required
                />
              </Grid>
            </Grid>
            <Label className='border-bottom padding-bottom-0' htmlFor='attachments'>
              <span id='attachments_label'>Attachments</span>
              <Tooltip
                name='publicNoticesAttachments'
                header='Attachments'
                content={tooltipContent}
                iconStyle={{ marginLeft: '5px' }}
              />
            </Label>
            <div className='margin-top-1 margin-bottom-1'>
              <FileInput
                deleteFileCallback={handleDeleteFile}
                id='attachments'
                multiple={true}
                name='attachments'
                onChangeCallback={handleFileInput}
                setFileErrors={setFileErrors}
                required
                value={stagedFiles}
              />
            </div>
          </div>
          <div className='border-bottom padding-bottom-0 margin-top-2'>
            <span id='security_check_label'>Security Check</span>
            <Tooltip
              name='security-check-tooltip'
              header='Security Check'
              content={SecurityCheckTooltipContent}
              iconStyle={{ marginLeft: '5px' }}
            />
          </div>
          <Antispam onBlur={handleAntispamBlur} />
        </section>
      </FormProvider>
      <footer className='primary-modal-footer'>
        <Button onClick={handleClose} base>
          Cancel
        </Button>
        <LoaderButton className='submit-comment-btn' onClick={handleSave} isLoading={isSaving || isSavingAttachments}>
          Submit
        </LoaderButton>
      </footer>
    </>
  );
});

export default PublicNoticeComment;
